// The cards that hang off a customer profile: the revenue split, pricing,
// clinic bill rates, contacts and collection sites.
// Each edits in place, since each is a short record and bouncing to a
// separate route for a phone number would be worse than the inline form.
//
// The revenue split is set by admin and read by every role: a clinic's bill
// rates reflect it, and the card says so. Clinic bill rates are entered by
// admin and read by every role, so a clinic knows what to bill us.

import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  listTestTypes,
  getCustomerPricing,
  addCustomerPricing,
  removeCustomerPricing,
  getClinicBillRates,
  addClinicBillRate,
  removeClinicBillRate,
  setCustomerSplit,
  addContact,
  removeContact,
  addSite,
  removeSite,
} from '../api.js'
import { useApiForm } from '../useApiForm.js'
import { useAuth } from '../auth.jsx'
import { ACTION } from '../../../shared/permissions.js'
import {
  Button,
  ButtonLink,
  DateField,
  Detail,
  EmptyState,
  Input,
  Notice,
  Panel,
  Pill,
  Select,
} from '../components/ui/index.js'
import { useConfirm } from '../confirm.jsx'
import { customerStatusTone } from '../tones/customers.js'
import { formatPrice, formatDate, statusLabel, SEPARATOR } from '../vocab.js'

export function SplitCard({ customer, canWrite, onChanged }) {
  const [value, setValue] = useState(customer.splitFranchiseePct)
  const [saved, setSaved] = useState(false)
  const { saving, error, fieldErrors, submit } = useApiForm()

  async function onSubmit(event) {
    event.preventDefault()
    const result = await submit(() => setCustomerSplit(customer.id, value))
    if (result) {
      setSaved(true)
      onChanged(result.customer)
    }
  }

  const reflects = `Your Clinic Bill Rates reflect this Revenue Percentage Split for ${customer.name}.`

  if (!canWrite) {
    return (
      <Panel title="Revenue Split">
        <Detail label="Clinic Bill Rate">
          <span className="tabular">{customer.splitFranchiseePct}%</span>
        </Detail>
        <p className="card-note muted">{reflects}</p>
      </Panel>
    )
  }

  return (
    <Panel title="Revenue Split">
      <form onSubmit={onSubmit} noValidate>
        {error ? <Notice tone="bad">{error}</Notice> : null}
        {saved && !error ? <Notice tone="ok">Split saved.</Notice> : null}

        <Input
          id="split"
          label="Clinic Bill Rate"
          type="number"
          step="0.01"
          min="0"
          max="100"
          value={value}
          onChange={(event) => {
            setSaved(false)
            setValue(event.target.value)
          }}
          error={fieldErrors.splitFranchiseePct}
          help={reflects}
        />

        <div className="btnrow">
          <span className="spacer" />
          <Button type="submit" variant="primary" block disabled={saving}>
            {saving ? 'Saving' : 'Save Split'}
          </Button>
        </div>
      </form>
    </Panel>
  )
}

// A dated rate list: one row per test type and effective date, no end date,
// the row in force today shown per type with the history behind it. Two
// lists share the shape: what the customer pays FTFS (pricing, with cost of
// goods) and what the clinic bills FTFS (bill rates). Resolution happens on
// the server, in server/pricing.js; this card only displays and edits.
const RATE_KINDS = {
  pricing: {
    idPrefix: 'price',
    title: 'Pricing',
    support:
      'Negotiated per test type. Where no row exists, the catalog default applies. Activity snapshots the price in force, so history never moves.',
    emptyTitle: 'No negotiated prices',
    emptyBody: () => 'Every test bills at the catalog default until a price is set here.',
    addLabel: 'Add price',
    unitHelp: undefined,
    confirmRemove: 'Remove this price row? Recorded activity keeps its snapshot.',
    loadFailed: 'Could not load the pricing.',
    withCogs: true,
    withNotes: false,
    list: async (customerId) => (await getCustomerPricing(customerId)).pricing,
    add: addCustomerPricing,
    remove: removeCustomerPricing,
  },
  billRates: {
    idPrefix: 'rate',
    title: 'Clinic Bill Rates',
    support:
      'What the clinic bills FTFS per test for this customer. The rate in force today is shown per test; the dated history sits behind it.',
    emptyTitle: 'No bill rates yet',
    emptyBody: (canWrite) =>
      canWrite
        ? 'Add the rate the clinic bills FTFS for each test.'
        : 'National Accounts has not set bill rates for this customer yet.',
    addLabel: 'Add bill rate',
    unitHelp: 'What the clinic bills FTFS for one of this test.',
    confirmRemove: 'Remove this bill rate row?',
    loadFailed: 'Could not load the bill rates.',
    withCogs: false,
    withNotes: true,
    list: async (customerId) => (await getClinicBillRates(customerId)).rates,
    add: addClinicBillRate,
    remove: removeClinicBillRate,
  },
}

const emptyRateForm = (withCogs) => ({
  testTypeId: '',
  unitPrice: '',
  effectiveDate: '',
  ...(withCogs ? { cogs: '' } : {}),
})

function RateCard({ customerId, canWrite, kind }) {
  const confirm = useConfirm()
  const [testTypes, setTestTypes] = useState([])
  const [entries, setEntries] = useState(null)
  const [loadError, setLoadError] = useState(null)
  const [adding, setAdding] = useState(false)
  const [expanded, setExpanded] = useState(null)
  const [form, setForm] = useState(emptyRateForm(kind.withCogs))
  const { saving, error, fieldErrors, submit } = useApiForm()

  // The entries carry their test names, so only the add form needs the
  // catalog, and only a writer holds the grant to list it.
  const load = useCallback(async () => {
    const [current, catalog] = await Promise.all([
      kind.list(customerId),
      canWrite ? listTestTypes() : Promise.resolve({ testTypes: [] }),
    ])
    setLoadError(null)
    setEntries(current)
    setTestTypes(catalog.testTypes)
  }, [customerId, canWrite, kind])

  useEffect(() => {
    load().catch((failure) => {
      setEntries([])
      setLoadError(failure.message || kind.loadFailed)
    })
  }, [load, kind])

  async function onSubmit(event) {
    event.preventDefault()
    const payload = { ...form }
    if (kind.withCogs) payload.cogs = form.cogs === '' ? null : form.cogs
    const result = await submit(() => kind.add(customerId, payload))
    if (result) {
      setForm(emptyRateForm(kind.withCogs))
      setAdding(false)
      load().catch((failure) => setLoadError(failure.message || kind.loadFailed))
    }
  }

  async function drop(row) {
    if (!(await confirm(kind.confirmRemove))) return
    try {
      await kind.remove(customerId, row.id)
      await load()
    } catch (failure) {
      setLoadError(failure.message || kind.loadFailed)
    }
  }

  const rowMeta = (row) =>
    [
      `Effective ${formatDate(row.effectiveDate)}`,
      formatPrice(row.unitPrice),
      row.cogs ? `cost ${formatPrice(row.cogs)}` : null,
      kind.withNotes ? row.notes : null,
    ]
      .filter(Boolean)
      .join(SEPARATOR)

  const listed = entries ?? []

  return (
    <Panel title={kind.title} support={kind.support}>
      {error ? <Notice tone="bad">{error}</Notice> : null}
      {loadError ? <Notice tone="bad">{loadError}</Notice> : null}

      {entries && listed.length === 0 && !adding && !loadError ? (
        <EmptyState title={kind.emptyTitle}>{kind.emptyBody(canWrite)}</EmptyState>
      ) : null}

      {listed.length > 0 ? (
        <div className="rows">
          {listed.map((entry) => {
            const current = entry.rows.find((row) => row.id === entry.currentRowId)
            const isOpen = expanded === entry.testTypeId
            return (
              <div key={entry.testTypeId}>
                <div className="row">
                  <div className="row-main">
                    <div className="row-primary">{entry.testType.name}</div>
                    <div className="row-meta">
                      {current
                        ? [
                            `Since ${formatDate(current.effectiveDate)}`,
                            current.cogs ? `cost ${formatPrice(current.cogs)}` : null,
                          ]
                            .filter(Boolean)
                            .join(SEPARATOR)
                        : 'Scheduled, not yet in force'}
                    </div>
                  </div>
                  <div className="row-trail">
                    <span className="tabular">
                      {current ? formatPrice(current.unitPrice) : null}
                    </span>
                    <Button
                      variant="ghost"
                      onClick={() => setExpanded(isOpen ? null : entry.testTypeId)}
                    >
                      {isOpen ? 'Hide history' : `History (${entry.rows.length})`}
                    </Button>
                  </div>
                </div>
                {isOpen
                  ? entry.rows.map((row) => (
                      <div key={row.id} className="row row-indent">
                        <div className="row-main">
                          <div className="row-meta">{rowMeta(row)}</div>
                        </div>
                        {canWrite ? (
                          <div className="row-trail">
                            <Button variant="ghost" onClick={() => drop(row)}>
                              Remove
                            </Button>
                          </div>
                        ) : null}
                      </div>
                    ))
                  : null}
              </div>
            )
          })}
        </div>
      ) : null}

      {canWrite && adding ? (
        <form className="card-form" onSubmit={onSubmit} noValidate>
          <div className="formgrid">
            <Select
              id={`${kind.idPrefix}-test`}
              className="span-2"
              label="Test type"
              value={form.testTypeId}
              onChange={(testTypeId) => setForm({ ...form, testTypeId })}
              error={fieldErrors.testTypeId}
              placeholder="Choose a test"
              options={[
                { value: '', label: 'Choose a test' },
                ...testTypes
                  .filter((testType) => testType.isActive)
                  .map((testType) => ({ value: testType.id, label: testType.name })),
              ]}
            />
            <Input
              id={`${kind.idPrefix}-unit`}
              label="Unit price"
              type="number"
              step="0.01"
              min="0"
              value={form.unitPrice}
              onChange={(event) => setForm({ ...form, unitPrice: event.target.value })}
              error={fieldErrors.unitPrice}
              help={kind.unitHelp}
            />
            {kind.withCogs ? (
              <Input
                id={`${kind.idPrefix}-cogs`}
                label="Cost of goods"
                type="number"
                step="0.01"
                min="0"
                value={form.cogs}
                onChange={(event) => setForm({ ...form, cogs: event.target.value })}
                help="Leave blank where no lab cost applies."
              />
            ) : null}
            <DateField
              id={`${kind.idPrefix}-date`}
              label="Effective date"
              value={form.effectiveDate}
              onChange={(effectiveDate) => setForm({ ...form, effectiveDate })}
              error={fieldErrors.effectiveDate}
            />
          </div>
          <div className="btnrow">
            <Button variant="ghost" onClick={() => setAdding(false)}>
              Cancel
            </Button>
            <span className="spacer" />
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? 'Adding' : kind.addLabel}
            </Button>
          </div>
        </form>
      ) : canWrite ? (
        <div className="btnrow">
          <Button onClick={() => setAdding(true)}>{kind.addLabel}</Button>
        </div>
      ) : null}
    </Panel>
  )
}

// Shown only to roles that manage pricing, so the card always edits.
export const PricingCard = ({ customerId }) => (
  <RateCard customerId={customerId} canWrite kind={RATE_KINDS.pricing} />
)

export const ClinicBillRatesCard = ({ customerId, canWrite }) => (
  <RateCard customerId={customerId} canWrite={canWrite} kind={RATE_KINDS.billRates} />
)

export function ContactsCard({ customerId, contacts, onChanged }) {
  const confirm = useConfirm()
  const canWrite = useAuth().can(ACTION.CUSTOMERS_WRITE)
  const [adding, setAdding] = useState(false)
  const [form, setForm] = useState({ name: '', role: '', email: '', phone: '' })
  const [removeError, setRemoveError] = useState(null)
  const { saving, error, fieldErrors, submit } = useApiForm()

  async function onSubmit(event) {
    event.preventDefault()
    const result = await submit(() => addContact(customerId, form))
    if (result) {
      setForm({ name: '', role: '', email: '', phone: '' })
      setAdding(false)
      onChanged()
    }
  }

  async function drop(contact) {
    if (!(await confirm(`Remove ${contact.name}?`))) return
    setRemoveError(null)
    try {
      await removeContact(customerId, contact.id)
      onChanged()
    } catch (caught) {
      setRemoveError(caught.message)
    }
  }

  return (
    <Panel
      title="Contacts"
      support="Who to call. Mark the DER, accounts payable and site contacts."
    >
      {removeError ? <Notice tone="bad">{removeError}</Notice> : null}
      {contacts.length === 0 && !adding ? (
        <EmptyState title="No contacts yet">
          {canWrite
            ? 'Add the person you deal with so nobody has to search their inbox for it.'
            : 'Nobody has recorded a contact for this account.'}
        </EmptyState>
      ) : null}

      {contacts.length > 0 ? (
        <div className="rows">
          {contacts.map((contact) => (
            <div key={contact.id} className="row">
              <div className="row-main">
                <div className="row-primary">
                  {contact.name}
                  {contact.isPrimary ? (
                    <>
                      {' '}
                      <Pill tone="ok">Primary</Pill>
                    </>
                  ) : null}
                </div>
                <div className="row-meta">
                  {[contact.role, contact.email, contact.phone].filter(Boolean).join(SEPARATOR) ||
                    'No details'}
                </div>
              </div>
              <div className="row-trail">
                {canWrite ? (
                  <Button variant="ghost" onClick={() => drop(contact)}>
                    Remove
                  </Button>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {adding ? (
        <form className="card-form" onSubmit={onSubmit} noValidate>
          {error ? <Notice tone="bad">{error}</Notice> : null}
          <div className="formgrid">
            <Input
              id="contact-name"
              label="Name"
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
              error={fieldErrors.name}
            />
            <Input
              id="contact-role"
              label="Role"
              value={form.role}
              onChange={(event) => setForm({ ...form, role: event.target.value })}
              placeholder="DER, accounts payable, site contact"
            />
            <Input
              id="contact-email"
              label="Email"
              type="email"
              value={form.email}
              onChange={(event) => setForm({ ...form, email: event.target.value })}
              error={fieldErrors.email}
            />
            <Input
              id="contact-phone"
              label="Phone"
              value={form.phone}
              onChange={(event) => setForm({ ...form, phone: event.target.value })}
            />
          </div>
          <div className="btnrow">
            <Button variant="ghost" onClick={() => setAdding(false)}>
              Cancel
            </Button>
            <span className="spacer" />
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? 'Adding' : 'Add contact'}
            </Button>
          </div>
        </form>
      ) : canWrite ? (
        <div className="btnrow">
          <Button
            onClick={() => {
              setRemoveError(null)
              setAdding(true)
            }}
          >
            Add contact
          </Button>
        </div>
      ) : null}
    </Panel>
  )
}

export function SitesCard({ customerId, sites, franchisees, onChanged }) {
  const confirm = useConfirm()
  const canWrite = useAuth().can(ACTION.CUSTOMERS_WRITE)
  const [adding, setAdding] = useState(false)
  const [form, setForm] = useState({ name: '', city: '', state: '', franchiseeId: '' })
  const [removeError, setRemoveError] = useState(null)
  const { saving, error, fieldErrors, submit } = useApiForm()

  async function onSubmit(event) {
    event.preventDefault()
    const result = await submit(() =>
      addSite(customerId, { ...form, franchiseeId: form.franchiseeId || null }),
    )
    if (result) {
      setForm({ name: '', city: '', state: '', franchiseeId: '' })
      setAdding(false)
      onChanged()
    }
  }

  async function drop(site) {
    if (!(await confirm(`Remove ${site.name}?`))) return
    setRemoveError(null)
    try {
      await removeSite(customerId, site.id)
      onChanged()
    } catch (caught) {
      setRemoveError(caught.message)
    }
  }

  return (
    <Panel title="Collection sites" support="Where collections happen, and who covers each one.">
      {removeError ? <Notice tone="bad">{removeError}</Notice> : null}
      {sites.length === 0 && !adding ? (
        <EmptyState title="No sites yet">
          {canWrite
            ? 'Add the locations this customer sends donors to.'
            : 'No collection sites are recorded for this account.'}
        </EmptyState>
      ) : null}

      {sites.length > 0 ? (
        <div className="rows">
          {sites.map((site) => (
            <div key={site.id} className="row">
              <div className="row-main">
                <div className="row-primary">{site.name}</div>
                <div className="row-meta">
                  {[
                    [site.city, site.state].filter(Boolean).join(', ') || 'No location',
                    site.franchiseeName,
                  ]
                    .filter(Boolean)
                    .join(SEPARATOR)}
                </div>
              </div>
              <div className="row-trail">
                {canWrite ? (
                  <Button variant="ghost" onClick={() => drop(site)}>
                    Remove
                  </Button>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {adding ? (
        <form className="card-form" onSubmit={onSubmit} noValidate>
          {error ? <Notice tone="bad">{error}</Notice> : null}
          <div className="formgrid">
            <Input
              id="site-name"
              label="Site name"
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
              error={fieldErrors.name}
            />
            <Input
              id="site-city"
              label="City"
              value={form.city}
              onChange={(event) => setForm({ ...form, city: event.target.value })}
            />
            <Input
              id="site-state"
              label="State"
              value={form.state}
              onChange={(event) => setForm({ ...form, state: event.target.value })}
            />
            <Select
              id="site-franchisee"
              label="Covered by"
              value={form.franchiseeId}
              onChange={(franchiseeId) => setForm({ ...form, franchiseeId })}
              options={[
                { value: '', label: 'Not assigned' },
                ...franchisees.map((franchisee) => ({
                  value: franchisee.id,
                  label: franchisee.name,
                })),
              ]}
            />
          </div>
          <div className="btnrow">
            <Button variant="ghost" onClick={() => setAdding(false)}>
              Cancel
            </Button>
            <span className="spacer" />
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? 'Adding' : 'Add site'}
            </Button>
          </div>
        </form>
      ) : canWrite ? (
        <div className="btnrow">
          <Button
            onClick={() => {
              setRemoveError(null)
              setAdding(true)
            }}
          >
            Add site
          </Button>
        </div>
      ) : null}
    </Panel>
  )
}

// A TPA's own customers. Each is a customer record of its own, reached from
// here rather than from the directory, and created with the TPA preset.
export function SubCustomersCard({ customer, canWrite }) {
  const listed = customer.subCustomers ?? []
  return (
    <Panel
      title="Customers"
      support="The customers this TPA books onsites for. Each has its own setup page."
    >
      {listed.length === 0 ? (
        <EmptyState title="No customers listed yet">
          {canWrite ? 'Add the customers this TPA administers.' : 'None recorded for this TPA.'}
        </EmptyState>
      ) : (
        <div className="rows">
          {listed.map((sub) => (
            <div key={sub.id} className="row">
              <div className="row-main">
                <div className="row-primary">
                  <Link to={`/customers/${sub.id}`}>{sub.name}</Link>
                </div>
                <div className="row-meta">
                  {sub.customerSince
                    ? `Since ${formatDate(sub.customerSince)}`
                    : 'Start date not set'}
                </div>
              </div>
              <div className="row-trail">
                <Pill tone={customerStatusTone[sub.status]}>{statusLabel(sub.status)}</Pill>
              </div>
            </div>
          ))}
        </div>
      )}
      {canWrite ? (
        <div className="btnrow">
          <span className="spacer" />
          <ButtonLink to={`/customers/new?tpa=${customer.id}`}>Add customer</ButtonLink>
        </div>
      ) : null}
    </Panel>
  )
}
